import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  RefreshCw, 
  Check, 
  X, 
  Upload,
  Bell,
  BellOff,
  Clock,
  History
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminChangesPage() {
  const [changes, setChanges] = useState([]);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [syncingId, setSyncingId] = useState(null);

  const fetchChanges = async () => {
    const token = localStorage.getItem('shrt_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/admin/pending-changes?include_history=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChanges(response.data.changes || []);
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch changes:', error);
      if (error.response?.status !== 401) {
        toast.error('Änderungen konnten nicht geladen werden');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    const token = localStorage.getItem('shrt_token');
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailNotifications(response.data.notify_on_changes ?? true);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  useEffect(() => {
    fetchChanges();
    fetchSettings();
  }, []);

  const handleToggleNotifications = async () => {
    setSavingSettings(true);
    try {
      const token = localStorage.getItem('shrt_token');
      await axios.put(`${API_URL}/api/admin/settings`, {
        notify_on_changes: !emailNotifications
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailNotifications(!emailNotifications);
      toast.success(emailNotifications ? 'E-Mail-Benachrichtigungen deaktiviert' : 'E-Mail-Benachrichtigungen aktiviert');
    } catch (error) {
      toast.error('Einstellung konnte nicht gespeichert werden');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSyncToMonday = async (changeId) => {
    setSyncingId(changeId);
    try {
      const token = localStorage.getItem('shrt_token');
      const response = await axios.post(`${API_URL}/api/sync/monday/sync-change/${changeId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.warning) {
        toast.warning(response.data.message);
      } else {
        toast.success(response.data.message);
      }
      fetchChanges();
    } catch (error) {
      const message = error.response?.data?.detail || 'Sync fehlgeschlagen';
      toast.error(message);
    } finally {
      setSyncingId(null);
    }
  };

  const handleDismiss = async (changeId, skipConfirm = false) => {
    if (!skipConfirm && !window.confirm('Änderung verwerfen ohne zu synchronisieren?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('shrt_token');
      await axios.delete(`${API_URL}/api/admin/pending-changes/${changeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!skipConfirm) toast.success('Änderung verworfen');
      fetchChanges();
    } catch (error) {
      toast.error('Fehler beim Verwerfen');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-CH');
  };

  const formatSwissPhone = (phone) => {
    if (!phone || phone === '(leer)') return phone;
    const digits = phone.replace(/[^\d+]/g, '');
    let raw = digits;
    if (raw.startsWith('0') && !raw.startsWith('00')) raw = '+41' + raw.substring(1);
    else if (raw.startsWith('0041')) raw = '+41' + raw.substring(4);
    else if (raw.startsWith('41') && !raw.startsWith('+')) raw = '+' + raw;
    if (raw.startsWith('+')) {
      const d = raw.substring(1);
      if (d.length === 11 && d.startsWith('41'))
        return `+${d.substring(0,2)} ${d.substring(2,4)} ${d.substring(4,7)} ${d.substring(7,9)} ${d.substring(9,11)}`;
    }
    return phone;
  };

  const isPhoneField = (field) => {
    const f = (field || '').toLowerCase();
    return f.includes('tel') || f.includes('phone') || f.includes('mobile') || f.includes('handy');
  };

  const getDisplayValue = (value, fieldName) => {
    if (value === null || value === undefined) return '(leer)';
    let text;
    if (typeof value === 'object') {
      text = typeof value.text === 'string' ? value.text : JSON.stringify(value);
    } else {
      text = String(value);
    }
    if (!text) return '(leer)';
    if (isPhoneField(fieldName)) return formatSwissPhone(text);
    return text;
  };

  // Group changes by member when submitted within 2 hours
  const groupedChanges = (() => {
    const groups = [];
    const used = new Set();
    const TWO_HOURS = 2 * 60 * 60 * 1000;
    
    for (let i = 0; i < changes.length; i++) {
      if (used.has(i)) continue;
      const c = changes[i];
      const group = { member_name: c.member_name, member_email: c.member_email, member_id: c.member_id, items: [c] };
      const cTime = new Date(c.submitted_at || c.created_at).getTime();
      
      for (let j = i + 1; j < changes.length; j++) {
        if (used.has(j)) continue;
        const other = changes[j];
        if (other.member_id === c.member_id) {
          const oTime = new Date(other.submitted_at || other.created_at).getTime();
          if (Math.abs(oTime - cTime) <= TWO_HOURS) {
            group.items.push(other);
            used.add(j);
          }
        }
      }
      used.add(i);
      groups.push(group);
    }
    return groups;
  })();

  return (
    <Layout>
      <div className=" animate-slide-in">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-foreground uppercase tracking-tight">
              Profil-Änderungen
            </h1>
            <p className="text-muted-foreground mt-1">
              Von Mitgliedern gemachte Änderungen zur Monday.com Synchronisation
            </p>
          </div>
          
          <Button
            onClick={() => { setLoading(true); fetchChanges(); }}
            variant="outline"
            className="rounded-none border-border"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={18} />
            Aktualisieren
          </Button>
        </div>

        {/* Email Notification Toggle */}
        <div className="bg-card border border-border p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {emailNotifications ? (
              <Bell className="text-green-500" size={20} />
            ) : (
              <BellOff className="text-muted-foreground" size={20} />
            )}
            <div>
              <p className="text-foreground font-medium">E-Mail-Benachrichtigungen</p>
              <p className="text-sm text-muted-foreground">
                {emailNotifications 
                  ? 'Sie erhalten eine E-Mail wenn Mitglieder ihre Daten ändern' 
                  : 'E-Mail-Benachrichtigungen sind deaktiviert'}
              </p>
            </div>
          </div>
          
          <Button
            onClick={handleToggleNotifications}
            disabled={savingSettings}
            variant={emailNotifications ? 'default' : 'outline'}
            className={`rounded-none ${emailNotifications ? 'bg-green-600 hover:bg-green-700' : ''}`}
            data-testid="toggle-notifications-btn"
          >
            {savingSettings ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : emailNotifications ? (
              'Aktiviert'
            ) : (
              'Deaktiviert'
            )}
          </Button>
        </div>

        {/* Changes List */}
        <div className="bg-card border border-border">
          <div className="p-4 border-b border-border">
            <h2 className="font-['Oswald'] text-lg font-bold text-foreground uppercase tracking-tight">
              Ausstehende Änderungen ({changes.length})
            </h2>
          </div>

          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-20 bg-secondary/30 animate-pulse" />
              ))}
            </div>
          ) : groupedChanges.length > 0 ? (
            <div className="divide-y divide-border">
              {groupedChanges.map((group) => (
                <div 
                  key={group.items[0].id} 
                  className="p-4 hover:bg-muted/50 transition-colors"
                  data-testid={`change-group-${group.items[0].id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="font-medium text-foreground">{group.member_name}</span>
                        <span className="text-muted-foreground text-sm">({group.member_email})</span>
                        {group.items.length > 1 && (
                          <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5">{group.items.length} Änderungen</span>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        {group.items.map((change) => (
                          <div key={change.id} className="bg-secondary/30 p-3 rounded-none" data-testid={`change-${change.id}`}>
                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                              {change.field_name}
                            </p>
                            <div className="flex items-center gap-2">
                              <span className="text-red-400 line-through">{getDisplayValue(change.old_value, change.field_name)}</span>
                              <span className="text-muted-foreground">→</span>
                              <span className="text-green-400">{getDisplayValue(change.new_value, change.field_name)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        <Clock size={12} />
                        {formatDate(group.items[0].submitted_at || group.items[0].created_at)}
                      </div>
                    </div>
                    
                    <div className="flex gap-2 flex-shrink-0">
                      {group.items.length > 1 ? (
                        <>
                          <Button
                            onClick={async () => {
                              for (const item of group.items) {
                                await handleSyncToMonday(item.id);
                              }
                            }}
                            disabled={group.items.some(item => syncingId === item.id)}
                            className="bg-blue-600 hover:bg-blue-700 text-foreground rounded-none"
                            data-testid={`sync-all-${group.items[0].id}`}
                          >
                            {group.items.some(item => syncingId === item.id) ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <>
                                <Upload className="mr-2" size={16} />
                                Alle freigeben
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={async () => {
                              if (!window.confirm(`Alle ${group.items.length} Änderungen von ${group.member_name} verwerfen?`)) return;
                              for (const item of group.items) {
                                await handleDismiss(item.id, true);
                              }
                              toast.success('Änderungen verworfen');
                              fetchChanges();
                            }}
                            variant="ghost"
                            className="text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-none"
                            data-testid={`dismiss-all-${group.items[0].id}`}
                          >
                            <X size={18} />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            onClick={() => handleSyncToMonday(group.items[0].id)}
                            disabled={syncingId === group.items[0].id}
                            className="bg-blue-600 hover:bg-blue-700 text-foreground rounded-none"
                            data-testid={`sync-${group.items[0].id}`}
                          >
                            {syncingId === group.items[0].id ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <>
                                <Upload className="mr-2" size={16} />
                                Freigeben & zu Monday
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => handleDismiss(group.items[0].id)}
                            variant="ghost"
                            className="text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-none"
                            data-testid={`dismiss-${group.items[0].id}`}
                          >
                            <X size={18} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center text-muted-foreground">
              <Check className="w-12 h-12 mx-auto mb-4 text-green-500" />
              <p>Keine ausstehenden Änderungen</p>
              <p className="text-sm mt-1">Alle Mitglieder-Änderungen wurden synchronisiert</p>
            </div>
          )}
        </div>

        {/* History Toggle */}
        <div className="mt-6">
          <Button
            variant="outline"
            onClick={() => setShowHistory(!showHistory)}
            className="rounded-none border-border mb-4"
            data-testid="toggle-history-btn"
          >
            <History className="mr-2" size={16} />
            {showHistory ? 'Protokoll ausblenden' : `Protokoll anzeigen (${history.length})`}
          </Button>

          {showHistory && history.length > 0 && (
            <div className="bg-card border border-border">
              <div className="p-4 border-b border-border">
                <h2 className="font-['Oswald'] text-lg font-bold text-foreground uppercase tracking-tight">
                  Änderungsprotokoll
                </h2>
              </div>
              <div className="divide-y divide-border">
                {history.map((change) => (
                  <div key={change.id} className="p-4 opacity-70" data-testid={`history-${change.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{change.member_name}</span>
                          <StatusBadge status={change.status} />
                        </div>
                        <div className="bg-secondary/30 p-2">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{change.field_name}</p>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-red-400 line-through">{getDisplayValue(change.old_value, change.field_name)}</span>
                            <span className="text-muted-foreground">→</span>
                            <span className="text-green-400">{getDisplayValue(change.new_value, change.field_name)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span><Clock size={11} className="inline mr-1" />{formatDate(change.synced_at || change.approved_at || change.rejected_at)}</span>
                          {change.approved_by && <span>von {change.approved_by}</span>}
                          {change.rejected_by && <span>von {change.rejected_by}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {showHistory && history.length === 0 && (
            <p className="text-sm text-muted-foreground">Noch kein Protokoll vorhanden.</p>
          )}
        </div>
      </div>
    </Layout>
  );
}

function StatusBadge({ status }) {
  const config = {
    synced: { label: 'Synchronisiert', cls: 'bg-green-500/10 text-green-500' },
    synced_local_only: { label: 'Lokal freigegeben', cls: 'bg-yellow-500/10 text-yellow-500' },
    approved: { label: 'Genehmigt', cls: 'bg-green-500/10 text-green-500' },
    rejected: { label: 'Abgelehnt', cls: 'bg-red-500/10 text-red-500' },
  };
  const c = config[status] || { label: status, cls: 'bg-muted text-muted-foreground' };
  return <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 ${c.cls}`}>{c.label}</span>;
}
